import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CacheService } from './CacheService';
import { isPlatformBrowser } from '@angular/common';
import { RetryService } from './RetryService';

interface chOptions {
  clearCache?: boolean;
  isLocalStorage?: boolean;
  ttl?: number,
  keyPrefix?: string,
  avoidAuthKey?: boolean
  cacheKey?: string
}

@Injectable({
  providedIn: 'root',
})
export class HttpCacheService {
  isBrowser: any;
  constructor(
    private http: HttpClient,
    private cs: CacheService,
    private retryService: RetryService,
    @Inject(PLATFORM_ID) platformId?: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  listPanelLoadDataMeta: any

  getBoxMethodFromEndpoint(endpoint: string): string | null {
    const segments = endpoint.split('/');
    return segments.length >= 4 ? segments[3] : null;
  }

  handleChOptions(chOptions?: chOptions, cacheKey?: string){
    if (!chOptions || !cacheKey || !this.isBrowser) return;

    if (chOptions?.clearCache) {
      this.cs.clearCacheKey(cacheKey);
      this.deleteCacheBasedOnKeyPrefix(chOptions.keyPrefix);
    }

    // this.cs.setLocalStorage(chOptions.isLocalStorage);

    if (chOptions.ttl) {
      this.cs.setTimeToLive(chOptions.ttl);
    }
  }

  deleteCacheBasedOnKey(cacheKey: string){
    console.log("[CACHE] delete key", cacheKey)
    this.cs.deleteCacheBasedOnKey(cacheKey);
  }

  deleteCacheBasedOnKeyPrefix(keyPrefix: string){
    this.cs.deleteCacheBasedOnKeyPrefix(keyPrefix);
  }

  /**
   * Private method to handle common retry logic for HTTP requests
   * @param requestFn The function that performs the actual HTTP request
   * @returns Promise with the response
   */
  private async executeWithRetryLogic(requestFn: () => Promise<any>): Promise<any> {
    try {
      // Configure retry parameters
      const maxRetries = 3;
      const initialDelayMs = 300;
      const backoffFactor = 2;
      const maxDelayMs = 5000;
      
      // Define which errors should be retried (network errors, specific 4xx errors, 5xx errors)
      const shouldRetry = (error: any) => {
        // Retry on network errors or server errors (5xx)
        const isNetworkError = !error.status;
        const isServerError = error.status >= 500 && error.status < 600;
        // Only retry specific 4xx errors that are likely to be resolved by retrying
        const isRetryable4xxError = error.status === 408 || // Request Timeout
                                   error.status === 429 || // Too Many Requests
                                   error.status === 425 || // Too Early
                                   error.status === 449;   // Retry With (Microsoft)
        return isNetworkError || isServerError || isRetryable4xxError;
      };
      
      return await this.retryService.retryWithBackoff(
        requestFn,
        maxRetries,
        initialDelayMs,
        backoffFactor,
        maxDelayMs,
        shouldRetry
      );
    } catch (error) {
      throw error;
    }
  }

  async get(url: string, options?: any, chOptions?: chOptions): Promise<any> {
    let cacheKey = null;
    if(this.isBrowser) cacheKey = this.cs.getCacheKey(url, options, null, chOptions);
    this.handleChOptions(chOptions, cacheKey);

    // console.log("[CACHE] chOptions", chOptions)
    if (this.isBrowser && this.cs.isValidData(cacheKey)) {
      console.log("[CACHE] get for key", cacheKey)
      return this.cs.getData(cacheKey, chOptions);
    } else {
      const executeWithRetry = async () => {
        if(!options) options = {};
        if(chOptions?.clearCache){
          if(!options.headers) options.headers = {};
          options.headers["Cache-Control"] = 'no-cache'
        }
        const response = await this.http.get(url, options).toPromise();
        try {this.cs.setData(cacheKey, response, chOptions); } catch (e){};
        return response;
      };
      
      return await this.executeWithRetryLogic(executeWithRetry);
    }
  }

  async post(url: string, body: any, options?: any,chOptions?: chOptions): Promise<any> {
    let cacheKey = null;
    if(this.isBrowser) cacheKey = this.cs.getCacheKey(url, options, body, chOptions);
    // console.log("[CACHE] chOptions", chOptions, body, this.isBrowser)
    // this.cs.isLocalStorage = true;
    this.handleChOptions(chOptions, cacheKey);
    // console.log('[CACHE] vkey', cacheKey);
    if (this.isBrowser && this.cs.isValidData(cacheKey)) {
      return this.cs.getData(cacheKey);
    } else {
      const executeWithRetry = async () => {
        if(!options) options = {};
        if(chOptions?.clearCache){
          if(!options.headers) options.headers = {};
          options.headers["Cache-Control"] = 'no-cache'
        }
        console.log('[CACHE] options', options);
        const response = await this.http.post(url, body, options).toPromise();
        try {this.cs.setData(cacheKey, response)} catch(e) {};
        this.listPanelLoadDataMeta = this.cs.getData(cacheKey)
        return response;
      };
      
      try {
        return await this.executeWithRetryLogic(executeWithRetry);
      } catch (error) {
        console.log("cache error : ",error)
        throw error;
      }
    }
  }
}
